import axios from 'axios';
import apiClient from './apiClient';

/**
 * Service fuer den Vertrags-PDF-Abruf des MVGen-Management-Backends. Es
 * gibt zwei Endpunkte, je nachdem ob ein finalisiertes DocStruct-PDF
 * existiert (`pdfAvailable`, serverseitig aus `docstruct_id IS NOT NULL`):
 *
 * - GET /api/contracts/{id}/pdf          — finalisierte Vertraege (DocStruct)
 * - GET /api/contracts/{id}/pdf/preview  — Vorschau nicht-finalisierter
 *   Vertraege (Entwuerfe), erzeugt live ueber mvgen
 *
 * Kein Caching (Projektstandard): Jeder Aufruf laedt das PDF erneut.
 *
 * Das Backend proxyt den jeweiligen Dienst; der Aufrufer muss die
 * Fehlerfaelle unterscheiden koennen (kein PDF vorhanden, Vertrag
 * unvollstaendig, Dienst nicht erreichbar). Dafuer wird jeder Fehler in
 * einen ContractPdfError mit `kind` uebersetzt.
 */

/** Unterscheidbare Fehlerursachen des PDF-Abrufs. */
export type ContractPdfErrorKind =
  /** HTTP 404: Vertrag unbekannt oder (noch) kein PDF vorhanden. */
  | 'not-found'
  /**
   * HTTP 422 (nur /pdf/preview): Vertrag ist fachlich unvollstaendig und
   * kann deshalb nicht als Vorschau gerendert werden. Kein technischer
   * Fehler, sondern ein normaler Zwischenzustand eines Entwurfs.
   */
  | 'incomplete'
  /** HTTP 502/503/504: PDF-Dienst nicht erreichbar. */
  | 'unavailable'
  /** Alles andere (Netzwerkfehler, unerwarteter Status, leerer Body). */
  | 'unknown';

/** Fehler des PDF-Abrufs mit unterscheidbarer Ursache. */
export class ContractPdfError extends Error {
  /** Ursache, an der die Anzeige ihre Meldung ausrichtet. */
  readonly kind: ContractPdfErrorKind;
  /**
   * Fehlende Pflichtfelder bei `kind === 'incomplete'` (sonst leer). Vom
   * Backend als `missingFields` geliefert; kann fehlen oder leer sein.
   */
  readonly missingFields: readonly string[];

  constructor(kind: ContractPdfErrorKind, message: string, missingFields: readonly string[] = []) {
    super(message);
    this.name = 'ContractPdfError';
    this.kind = kind;
    this.missingFields = missingFields;
  }
}

/** HTTP-Status, die auf einen nicht erreichbaren PDF-Dienst hindeuten. */
const UNAVAILABLE_STATUS: readonly number[] = [502, 503, 504];

/** Erwarteter MIME-Typ des Proxy-Endpunkts. */
const PDF_MIME_TYPE = 'application/pdf';

/**
 * Unspezifischer Binaertyp: Manche Proxys/Server liefern ihn statt
 * application/pdf. Er sagt nichts Gegenteiliges aus und wird deshalb wie
 * ein fehlender Typ behandelt.
 */
const GENERIC_BINARY_MIME_TYPE = 'application/octet-stream';

/**
 * Stellt sicher, dass der Blob als PDF in ein iframe darf.
 *
 * Sicherheitsgrund: Die Anzeige erzeugt aus dem Blob eine
 * blob:-URL. Diese erbt den Origin des Dokuments — ein Blob mit
 * `text/html` wuerde im iframe also als SAME-ORIGIN-HTML mit aktivem
 * JavaScript ausgefuehrt und koennte das JWT aus der sessionStorage
 * lesen. Ein fremder Typ ist deshalb ein Fehler und kein Anzeigefall.
 *
 * Fehlt der Typ (leer) oder ist er der unspezifische Binaertyp, wird der
 * Blob mit explizitem application/pdf NEU erzeugt: Das ist robust gegen
 * fehlende Content-Type-Header und legt den Typ auf einen fest, der im
 * iframe nichts ausfuehren kann.
 */
function ensurePdfBlob(data: Blob): Blob {
  const type = data.type.toLowerCase().split(';')[0].trim();
  if (type === PDF_MIME_TYPE) {
    return data;
  }
  if (type !== '' && type !== GENERIC_BINARY_MIME_TYPE) {
    throw new ContractPdfError('unknown', `Unerwarteter Content-Type: ${data.type}`);
  }
  return new Blob([data], { type: PDF_MIME_TYPE });
}

/** Vom Backend bei HTTP 422 gelieferter Fehlerbody (CONTRACT_INCOMPLETE). */
interface ContractIncompleteBody {
  readonly code?: string;
  readonly message?: string;
  readonly missingFields?: readonly string[];
}

/** Generische Meldung, falls der 422-Body nicht auswertbar ist. */
const INCOMPLETE_FALLBACK_MESSAGE = 'Der Vertrag ist unvollständig.';

/**
 * Baut den 'incomplete'-Fehler aus dem 422-Antwortbody.
 *
 * Wichtig: Bei `responseType: 'blob'` ist auch der FEHLER-Body ein Blob,
 * nicht das vom Backend gesendete JSON — der Browser parst ihn wegen
 * responseType nicht automatisch. Fuer die anderen Fehlerfaelle reicht der
 * HTTP-Status zur Unterscheidung, deshalb wird dort bewusst kein Blob
 * gelesen. Nur hier (422) enthaelt der Body zusaetzliche fachliche Angaben
 * (`missingFields`), die fuer eine konkrete Meldung noetig sind — deshalb
 * wird ausschliesslich in diesem Fall async `blob.text()` + `JSON.parse`
 * verwendet.
 *
 * Defensiv: Ein kaputter/leerer/unerwarteter Body darf keinen
 * unbehandelten Fehler werfen, sondern faellt auf eine generische Meldung
 * zurueck.
 */
async function toIncompleteError(data: unknown): Promise<ContractPdfError> {
  if (!(data instanceof Blob)) {
    return new ContractPdfError('incomplete', INCOMPLETE_FALLBACK_MESSAGE);
  }
  try {
    const text = await data.text();
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null) {
      return new ContractPdfError('incomplete', INCOMPLETE_FALLBACK_MESSAGE);
    }
    const body = parsed as ContractIncompleteBody;
    const missingFields = Array.isArray(body.missingFields)
      ? body.missingFields.filter((field): field is string => typeof field === 'string')
      : [];
    const message =
      missingFields.length > 0
        ? `${INCOMPLETE_FALLBACK_MESSAGE} Es fehlen: ${missingFields.join(', ')}.`
        : INCOMPLETE_FALLBACK_MESSAGE;
    return new ContractPdfError('incomplete', message, missingFields);
  } catch {
    // Kaputtes/leeres JSON: defensiv auf die generische Meldung
    // zurueckfallen statt einen unbehandelten Fehler zu werfen.
    return new ContractPdfError('incomplete', INCOMPLETE_FALLBACK_MESSAGE);
  }
}

/**
 * Uebersetzt einen beliebigen Fehler in einen ContractPdfError.
 *
 * Abbrueche (AbortController) bleiben unveraendert, damit der Aufrufer
 * sie weiterhin als Abbruch und nicht als Fehlerzustand erkennt.
 *
 * Async, weil der 422-Fall den Blob-Body auslesen muss (siehe
 * toIncompleteError); alle anderen Faelle werten nur den HTTP-Status aus.
 */
async function toContractPdfError(error: unknown): Promise<unknown> {
  if (error instanceof ContractPdfError || axios.isCancel(error)) {
    return error;
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 404) {
      return new ContractPdfError('not-found', 'Kein PDF fuer diesen Vertrag vorhanden');
    }
    if (status === 422) {
      return toIncompleteError(error.response?.data);
    }
    if (status !== undefined && UNAVAILABLE_STATUS.includes(status)) {
      return new ContractPdfError('unavailable', 'PDF-Dienst nicht erreichbar');
    }
  }
  return new ContractPdfError('unknown', 'PDF konnte nicht geladen werden');
}

/**
 * Laedt das PDF eines Mietvertrags als Blob (immer mit dem MIME-Typ
 * application/pdf, siehe ensurePdfBlob).
 *
 * `pdfAvailable` waehlt den Endpunkt: finalisierte Vertraege haben ein
 * fertiges DocStruct-PDF (`/pdf`), alle anderen (Entwuerfe) nur eine live
 * erzeugte Vorschau (`/pdf/preview`). Das entspricht `ContractSummary.
 * pdfAvailable`, das serverseitig aus `docstruct_id IS NOT NULL` stammt.
 *
 * Fehler werden als ContractPdfError geworfen (siehe
 * toContractPdfError); ein abgebrochener Request wirft den urspruenglichen
 * Abbruchfehler.
 */
export async function fetchContractPdf(
  contractId: string,
  pdfAvailable: boolean,
  signal?: AbortSignal
): Promise<Blob> {
  const path = pdfAvailable
    ? `/api/contracts/${encodeURIComponent(contractId)}/pdf`
    : `/api/contracts/${encodeURIComponent(contractId)}/pdf/preview`;
  try {
    const response = await apiClient.get<Blob>(path, { responseType: 'blob', signal });
    const data: Blob = response.data;
    if (!(data instanceof Blob) || data.size === 0) {
      // Status 200 mit leerem/unerwartetem Body: fuer die Anzeige ein Fehler,
      // sonst entstuende ein leeres iframe ohne jede Rueckmeldung.
      throw new ContractPdfError('unknown', 'Leere PDF-Antwort');
    }
    return ensurePdfBlob(data);
  } catch (error) {
    throw await toContractPdfError(error);
  }
}
